import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ProductsCartConfigModal } from "@/modules/products/components/ProductsCartConfigModal";
import type { SellerProductListItemView } from "@/lib/api/types";

type Props = {
  storeSlug: string;
  /** Products currently selected on the catalog. */
  selectedProducts: SellerProductListItemView[];
  onClear: () => void;
};

/**
 * Floating action bar shown while one or more products are selected on the
 * catalog. "Create cart link" opens a popup where the seller picks variants and
 * quantities before generating a multi-product checkout link ("cart"). One
 * product works too — it's just a one-line cart.
 */
export function ProductsBulkCartBar({
  storeSlug,
  selectedProducts,
  onClear,
}: Props) {
  const t = useTranslations();
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  if (selectedProducts.length === 0) return null;

  return (
    <>
      {/* Mobile keeps the bar above the in-flow bottom nav (~64px) + safe
          area; desktop (lg+) has no bottom nav so it hugs the viewport. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(76px_+_env(safe-area-inset-bottom,0px))] z-40 flex justify-center px-4 lg:bottom-6">
        <div className="pointer-events-auto flex max-w-[calc(100%-44px)] items-center gap-4 rounded-[15px] border border-[color:var(--border-light)] bg-[color:var(--surface)] py-[9px] pe-[9px] ps-4 text-[color:var(--text-primary)] shadow-[0_14px_38px_rgba(16,16,15,0.18),0_3px_10px_rgba(16,16,15,0.08)]">
          <div className="flex items-center gap-[11px] whitespace-nowrap text-[13px] font-semibold">
            <span className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px] bg-[color:var(--accent-500)] text-[12px] font-bold text-[color:var(--on-accent)]">
              {selectedProducts.length}
            </span>
            <span className="max-sm:hidden">
              {t(keys.products.bulkSelectedCount, {
                count: selectedProducts.length,
              })}
            </span>
          </div>
          <span
            aria-hidden="true"
            className="h-6 w-px shrink-0 bg-[color:var(--border-light)] max-sm:hidden"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClear}
              aria-label={t(keys.products.bulkClearSelection)}
              className="inline-flex min-h-10 items-center gap-[7px] whitespace-nowrap rounded-[10px] border border-[color:var(--border-strong)] bg-transparent px-3.5 py-2 text-[12.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] lg:min-h-0"
            >
              <XIcon />
              <span className="max-sm:hidden">
                {t(keys.products.bulkClearSelection)}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setIsConfigOpen(true)}
              className="inline-flex min-h-10 items-center gap-[7px] whitespace-nowrap rounded-[10px] bg-[color:var(--accent-500)] px-3.5 py-2 text-[12.5px] font-semibold text-[color:var(--on-accent)] transition-colors hover:bg-[color:var(--accent-600)] lg:min-h-0"
            >
              <CartIcon />
              {t(keys.products.bulkCartButton)}
            </button>
          </div>
        </div>
      </div>

      {isConfigOpen ? (
        <ProductsCartConfigModal
          storeSlug={storeSlug}
          products={selectedProducts}
          onClose={() => setIsConfigOpen(false)}
        />
      ) : null}
    </>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M2 3h2.2l2.2 12.3a2 2 0 0 0 2 1.7h8.4a2 2 0 0 0 2-1.6L22 8H6" />
    </svg>
  );
}
